import type { SceneContent } from '../../presentation/types';

export const opening: SceneContent = {
  id: 'opening',
  shortLabel: 'OPEN',
  title: 'The Opening',
  seconds: 40,
  brand: 'moderta',
  cinematic: true,
  bridgeIn: '',
  atmosphere: (step) =>
    step === 0 ? { pattern: 'ambient', density: 0.04, opacity: 0.015, cameraZ: 12.5 }
    : step === 1 ? { pattern: 'ambient', density: 0.14, opacity: 0.06, cameraZ: 13.4 }
    : { pattern: 'orbit', density: 0.18, opacity: 0.07, cameraZ: 14 },
  script: {
    title: 'The Opening',
    onScreen: [
      'AI · DATA · AUTOMATION · CLOUD · PEOPLE · PRODUCTS · WORKFLOWS',
      'MODERTA',
      'Ideas → Systems → Impact',
      'Building digital systems around real-world problems.',
    ],
    visual: 'Near darkness and a single breathing point of light. Points emerge around it and organise into a Three.js network while concept words surface and fade. The network collapses to one point and re-forms as a halo; MODERTA resolves letter by letter, followed by Ideas → Systems → Impact and the tagline.',
    narration: [
      'Every system starts as a single point. An idea, a question, a problem somebody cannot stop thinking about.',
      'Then the pieces start to appear. Data. People. Workflows. Cloud. Automation. AI. On their own, each of them is just a piece.',
      'The interesting part is what happens when they are connected on purpose.',
      'This is Moderta.',
      'We take ideas, turn them into working systems, and measure them by the impact they create.',
      'Put simply: we build digital systems around real-world problems.',
    ],
    presenterNotes: [
      'Let the room settle on the single point. Start speaking only after a beat: "Every system starts as a single point."',
      'Points emerge and form a network; concept words flash by. Name a few of them. No need to explain each one.',
      'The network collapses into the brand. Pause, then: "This is Moderta."',
      'Ideas → Systems → Impact. Read it slowly, one word at a time.',
      '"We build digital systems around real-world problems." Hold, then advance to the problem.',
    ],
  },
};

export const problem: SceneContent = {
  id: 'problem',
  shortLabel: 'PROBLEM',
  title: 'The Problem',
  seconds: 55,
  brand: 'moderta',
  accent: '#ff9d8a',
  bridgeIn: 'Before looking at what Moderta builds, look at the world those systems have to work in.',
  atmosphere: (step) =>
    step <= 2 ? { pattern: 'drift', density: 0.18 + step * 0.12, opacity: 0.07 + step * 0.03, cameraZ: 14 - step * 0.4 }
    : step === 3 ? { pattern: 'drift', density: 0.42, opacity: 0.035, cameraZ: 13 }
    : { pattern: 'orbit', density: 0.26, opacity: 0.08, cameraZ: 14 },
  script: {
    title: 'The Problem',
    onScreen: ['MORE TOOLS.', 'MORE DATA.', 'MORE COMPLEXITY.', "THE PROBLEM ISN'T ACCESS.", "IT'S CONNECTION."],
    visual: 'Ten system chips (documents, dashboards, APIs, databases, teams…) drift apart. Everyday noise multiplies around them, connections reach out and fail, and the whole field agitates. It freezes, then the systems line up on a ring and connect; the colour resolves from warning red to the brand green.',
    narration: [
      'Look at almost any organisation today. It does not have a shortage of software.',
      'There are documents, dashboards, spreadsheets, APIs, databases, cloud services, message threads, and teams working across all of them.',
      'Every year there are more tools, and every tool produces more data.',
      'And somewhere along the way, the work itself gets harder. Information exists, but it lives in five places. The report is in one system, the context is in another, and the decision is made in a meeting.',
      'So the problem usually is not access. People can reach the information.',
      'The problem is connection. Bringing the right information, the right people and the right workflow together around the decision that actually needs to be made.',
    ],
    presenterNotes: [
      'Scattered systems appear. "Organisations do not lack software." Point at a few of the objects.',
      'Everything multiplies. "Every tool produces more data."',
      'It becomes noisy; connections flicker but never complete. Let the chaos run for a second.',
      'Everything freezes. Deliver the line calmly: "The problem isn\'t access."',
      'The systems line up and connect. "It\'s connection." This is the thesis of the whole presentation.',
    ],
  },
};

export const approach: SceneContent = {
  id: 'approach',
  shortLabel: 'APPROACH',
  title: "Moderta's Approach",
  seconds: 40,
  brand: 'moderta',
  bridgeIn: 'If the problem is connection, the answer is not one more disconnected tool.',
  atmosphere: () => ({ pattern: 'orbit', density: 0.22, opacity: 0.08, cameraZ: 14.4 }),
  script: {
    title: "Moderta's Approach",
    onScreen: ['MODERTA', 'UNDERSTAND', 'BUILD', 'CONNECT', 'EVOLVE', 'Purpose-built products for real-world problems.'],
    visual: 'Moderta sits calm at the centre. Understand, Build and Connect arrive as an open line; Evolve closes it into a loop and a pulse starts travelling around it. The diagram steps back for the closing line.',
    narration: [
      'This is where Moderta starts. Not with a technology choice. With the problem.',
      'First, understand it: who is affected, where the information lives, and what a better outcome looks like.',
      'Then build the system around that understanding, and connect it to the data, tools and people it depends on.',
      'And then keep going. Real problems change, so the system has to evolve with them. That turns a one-off project into a loop that keeps improving.',
      'That is why Moderta builds purpose-built products rather than generic software. Each one starts from a real problem.',
    ],
    presenterNotes: [
      'Moderta at the centre, calm after the chaos. "This is where Moderta starts: with the problem."',
      'Understand, Build, Connect arrive in sequence. Walk through them briefly.',
      'Evolve closes the loop. "Real problems change, so the system keeps evolving."',
      '"Purpose-built products for real-world problems." Then move to the portfolio.',
    ],
  },
};
